import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, LogOut, ArrowRight, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

/**
 * Sign-in / sign-up page. If the visitor is already authenticated we show a
 * compact "Welcome back" card so they don't have to re-enter credentials —
 * this is critical now that JWT sessions last 30 days and a cookie can be
 * already valid even when the user clicks "Sign in" from the navbar.
 */
const SignedInHero = ({ user, isPro, onLogout, navigate }) => (
  <Card className="w-full max-w-md border-purple-200 shadow-md" data-testid="already-signed-in">
    <CardContent className="pt-8 pb-6 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
        <CheckCircle2 className="text-green-600" size={32} />
      </div>
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">You are signed in</p>
      <p className="text-xl font-bold text-gray-900">{user.name || user.email.split('@')[0]}</p>
      <p className="text-sm text-gray-500 mt-1">{user.email}</p>
      <div className="flex items-center justify-center gap-2 mt-3">
        {isPro ? (
          <Badge className="bg-purple-600 text-white">Pro Plan</Badge>
        ) : (
          <Badge variant="outline">Free Plan</Badge>
        )}
        <Badge variant="outline" className="text-[10px]">Session valid for 30 days</Badge>
      </div>

      <div className="grid gap-2 mt-6">
        <Button
          className="w-full bg-purple-600 hover:bg-purple-700"
          onClick={() => navigate('/dashboard')}
          data-testid="continue-to-dashboard-btn"
        >
          Continue to Dashboard
          <ArrowRight size={16} className="ml-1" />
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={onLogout}
          data-testid="auth-page-logout-btn"
        >
          <LogOut size={14} className="mr-1" />
          Sign out & use a different account
        </Button>
      </div>

      <p className="text-[11px] text-gray-400 mt-4">
        Need something else? <Link to="/" className="text-purple-600 hover:underline">Back to home</Link>
      </p>
    </CardContent>
  </Card>
);

const AuthPage = () => {
  const { login, signup, logout, user, isPro, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  // Capture ?ref= so the next signup attributes the referral (first-touch
  // wins; persisted in sessionStorage so social logins keep it after redirect).
  const refCode = searchParams.get('ref');
  // Optional ?next= for deep-link redirects after auth (e.g. ?next=/courses/abc).
  const nextPath = searchParams.get('next');

  useEffect(() => {
    if (refCode) {
      try {
        sessionStorage.setItem('neon_alpha_ref', refCode.toUpperCase());
      } catch (_) {
        /* sessionStorage may be unavailable in private browsing */
      }
    }
    if (searchParams.get('mode') === 'signup') {
      setMode('signup');
    }
  }, [refCode, searchParams]);

  // Auto-redirect to /dashboard (or ?next=) as soon as the user is resolved.
  // This handles BOTH (a) form submit success and (b) the user already had a
  // valid cookie when they hit /auth — previously the SignedInHero card was a
  // dead end requiring an extra click.
  useEffect(() => {
    if (authLoading) return;
    if (user) {
      const target = nextPath && nextPath.startsWith('/') ? nextPath : '/dashboard';
      navigate(target, { replace: true });
    }
  }, [authLoading, user, nextPath, navigate]);

  // REMINDER: DO NOT HARDCODE THE URL — Emergent Google Auth requires this exact callback shape.
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast({ title: 'Email and password are required', variant: 'destructive' });
      return;
    }

    try {
      setSubmitting(true);
      if (mode === 'login') {
        await login(form.email, form.password);
        toast({ title: 'Welcome back', description: 'Session valid for 30 days.' });
      } else {
        const stored = (() => {
          try {
            return sessionStorage.getItem('neon_alpha_ref') || undefined;
          } catch (_) {
            return undefined;
          }
        })();
        await signup({
          email: form.email,
          password: form.password,
          name: form.name || undefined,
          ref: stored,
        });
        try {
          sessionStorage.removeItem('neon_alpha_ref');
        } catch (_) {
          /* ignore */
        }
        toast({ title: 'Account created', description: 'You are now signed in.' });
      }
      navigate('/dashboard');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Authentication failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast({ title: 'Signed out' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 pt-16 flex items-center justify-center px-4">
      {user ? (
        // Auto-redirect effect above will take us to /dashboard; render a
        // lightweight placeholder rather than the legacy SignedInHero card
        // so the user never sees a "stuck on /auth" page.
        <Card className="w-full max-w-md border-purple-200 shadow-md" data-testid="already-signed-in">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <p className="text-sm text-gray-600">Redirecting to your dashboard…</p>
            <p className="text-xs text-gray-400 mt-1">{user.email}</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              {isPro ? (
                <Badge className="bg-purple-600 text-white">Pro Plan</Badge>
              ) : (
                <Badge variant="outline">Free Plan</Badge>
              )}
            </div>
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={handleLogout}
              data-testid="auth-page-logout-btn"
            >
              <LogOut size={14} className="mr-1" />
              Sign out & use a different account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full max-w-md shadow-md">
          <CardHeader>
            <CardTitle data-testid="auth-page-title">
              {mode === 'login' ? 'Sign in to Neon Alpha' : 'Create your account'}
            </CardTitle>
            <p className="text-xs text-gray-400 mt-1">
              {mode === 'login'
                ? 'Stay signed in for 30 days. Cookies are HttpOnly + Secure.'
                : 'Free plan includes 3 AI analyses per day. Upgrade to Pro any time.'}
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4 text-sm">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`px-3 py-1 rounded-full border text-xs ${
                  mode === 'login' ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-200 text-gray-600'
                }`}
                data-testid="mode-login-btn"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setMode('signup')}
                className={`px-3 py-1 rounded-full border text-xs ${
                  mode === 'signup' ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-200 text-gray-600'
                }`}
                data-testid="mode-signup-btn"
              >
                Sign up
              </button>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full mb-4 border-gray-300 hover:bg-gray-50"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" className="mr-2">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
                <path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A10.99 10.99 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 mb-4">
              <div className="h-px bg-gray-200 flex-1" />
              <span className="text-xs text-gray-400">or with email</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {mode === 'signup' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Name (optional)</label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    data-testid="signup-name-input"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  data-testid="auth-email-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
                <Input
                  name="password"
                  type="password"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  data-testid="auth-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 mt-2"
                disabled={submitting}
                data-testid="auth-submit-btn"
              >
                {submitting ? 'Please wait...' : mode === 'login' ? 'Sign in' : 'Create account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AuthPage;
